import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Volume2, VolumeX, ArrowRight, ChevronDown } from "lucide-react";
import { useBookCall } from "@/components/BookCallContext";
import HomeFlow from "@/components/HomeFlow";
import SEO from "@/components/SEO";
import { TESTIDS } from "@/constants/testIds";

const HOME_SCHEMA = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
        {
            "@type": "Question",
            "name": "What is KyroLabs?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "KyroLabs builds agentic AI for enterprise customer experience and contact centres. We deliver voice, chat and operations agents that plug into your business systems across travel, BFSI, consumer durables and BPO, grounded in your knowledge and always under human oversight.",
            },
        },
        {
            "@type": "Question",
            "name": "What does KyroLabs offer?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "Kyro Engage (omnichannel engagement fabric), Kyro Engine (agentic orchestration engine), Kyro Xchange (data + AEO intelligence), Kyro Shield (security & resilience), Kyro CX (AI CX suite), Kyro GCC Services, Kyro Foundry (custom AI) and Kyro Atlas (RAG grounding).",
            },
        },
        {
            "@type": "Question",
            "name": "Which industries does KyroLabs work with?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "Travel, Transport, Hospitality, Logistics, Telecom, Healthcare, BFSI and Consumer Durables. Contact hello@kyrolabs.ai to book a demo.",
            },
        },
    ],
};

const STATS = [
    { n: "140+", l: "languages, voice & text" },
    { n: "Real-time", l: "responses in split seconds" },
    { n: "24/7", l: "always answering" },
    { n: "100%", l: "of calls analysed" },
];

export default function Home() {
    const videoRef = useRef(null);
    const [muted, setMuted] = useState(true);
    const { openModal } = useBookCall();

    const toggleMute = () => {
        const v = videoRef.current;
        if (!v) return;
        v.muted = !v.muted;
        if (!v.muted) v.play().catch(() => {});
        setMuted(v.muted);
    };

    const scrollTo = (id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <>
            <SEO
                path="/"
                title="KyroLabs, Agentic AI for Enterprise CX & Contact Centres"
                description="KyroLabs builds agentic AI for enterprise customer experience and contact centres, voice, chat and operations agents across travel, BFSI, consumer durables and BPO."
                schema={HOME_SCHEMA}
            />
            {/* HERO, split: text LEFT, video RIGHT (small container so it never pixelates) */}
            <header
                data-testid={TESTIDS.hero.root}
                style={{
                    background: "var(--bg)",
                    borderBottom: "1px solid var(--line)",
                    paddingTop: 40,
                    paddingBottom: 40,
                    position: "relative",
                    overflow: "hidden",
                }}
            >
                <div className="aurora-bg" style={{ opacity: 0.55 }} />
                <div className="wrap" style={{ position: "relative", zIndex: 2 }}>
                    <div
                        className="hero-split"
                        style={{
                            display: "grid",
                            gridTemplateColumns: "minmax(0,480px) 1fr",
                            gap: 48,
                            alignItems: "center",
                            minHeight: "70vh",
                        }}
                    >
                        {/* LEFT, text (narrower + a bit indented from left edge) */}
                        <motion.div
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, ease: "easeOut" }}
                            style={{ paddingLeft: 8 }}
                        >
                            <div
                                className="font-mono-label"
                                style={{
                                    color: "var(--kl-muted)",
                                    marginBottom: 16,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 10,
                                }}
                            >
                                <span
                                    style={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        background: "var(--good)",
                                        boxShadow: "0 0 12px rgba(95,224,160,0.7)",
                                    }}
                                />
                                KyroLabs · enterprise AI agents
                            </div>
                            <h1
                                data-testid={TESTIDS.hero.tagline}
                                className="font-display"
                                style={{
                                    fontSize: "clamp(2.4rem, 5.4vw, 4.4rem)",
                                    color: "var(--ink)",
                                    maxWidth: "14ch",
                                    lineHeight: 1.02,
                                    marginBottom: 22,
                                    fontWeight: 600,
                                    letterSpacing: "-0.025em",
                                }}
                            >
                                We build agents for the job.
                            </h1>
                            <p
                                className="lede"
                                style={{ maxWidth: "44ch", marginBottom: 30 }}
                            >
                                Tell us your use case. We&apos;ll build the agent across voice, chat,
                                enterprise knowledge and business systems.
                            </p>
                            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                                <button
                                    data-testid={TESTIDS.hero.bookCall}
                                    className="btn btn-p"
                                    onClick={() => openModal({ source: "hero" })}
                                >
                                    Book a Demo <ArrowRight size={14} />
                                </button>
                                <button
                                    data-testid={TESTIDS.hero.what}
                                    className="btn btn-ghost"
                                    onClick={() => scrollTo("what")}
                                >
                                    What we do
                                    <ChevronDown size={14} />
                                </button>
                            </div>
                        </motion.div>

                        {/* RIGHT, landscape video card (16:9) */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.97 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.9, ease: "easeOut" }}
                            style={{
                                position: "relative",
                                borderRadius: 20,
                                overflow: "hidden",
                                border: "1px solid var(--line-strong)",
                                background: "#08090c",
                                aspectRatio: "16 / 10",
                                width: "100%",
                                boxShadow: "0 30px 80px rgba(0,0,0,0.55)",
                            }}
                        >
                            <video
                                ref={videoRef}
                                data-testid={TESTIDS.hero.video}
                                autoPlay
                                muted
                                loop
                                playsInline
                                preload="auto"
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    display: "block",
                                }}
                            >
                                <source src="/assets/hero.mp4" type="video/mp4" />
                            </video>
                            <div
                                style={{
                                    position: "absolute",
                                    inset: 0,
                                    background:
                                        "linear-gradient(180deg, rgba(8,9,12,0.10) 0%, rgba(8,9,12,0) 40%, rgba(8,9,12,0.55) 100%)",
                                    pointerEvents: "none",
                                }}
                            />
                            <button
                                data-testid={TESTIDS.hero.mute}
                                onClick={toggleMute}
                                aria-label={muted ? "Unmute" : "Mute"}
                                style={{
                                    position: "absolute",
                                    right: 16,
                                    bottom: 16,
                                    width: 40,
                                    height: 40,
                                    borderRadius: "50%",
                                    border: "1px solid rgba(255,255,255,0.35)",
                                    background: "rgba(8,9,12,0.55)",
                                    backdropFilter: "blur(8px)",
                                    color: "#fff",
                                    cursor: "pointer",
                                    display: "grid",
                                    placeItems: "center",
                                }}
                            >
                                {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                            </button>
                        </motion.div>
                    </div>
                </div>
                <style>{`
                    @media (max-width: 880px) {
                        .hero-split { grid-template-columns: 1fr !important; min-height: auto !important; }
                    }
                `}</style>
            </header>

            {/* WHAT WE DO */}
            <section
                id="what"
                data-testid={TESTIDS.home.what}
                style={{ padding: "100px 0 60px", position: "relative" }}
            >
                <div className="wrap">
                    <div style={{ marginBottom: 22 }}>
                        <span className="kicker">How it works</span>
                        <h2 className="h2" style={{ margin: "12px 0 14px" }}>
                            We build agents for the job.
                        </h2>
                        <p className="lede">
                            Tell us your use case. We build the agent across voice, chat, enterprise
                            knowledge and business systems.
                        </p>
                    </div>
                    <HomeFlow />
                </div>
            </section>

            {/* STATS */}
            <section style={{ padding: "60px 0" }}>
                <div className="wrap">
                    <div
                        data-testid={TESTIDS.home.stats}
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(4, 1fr)",
                            gap: 16,
                        }}
                        className="stats-grid"
                    >
                        {STATS.map((s, i) => (
                            <motion.div
                                key={i}
                                data-testid={TESTIDS.home.statItem(i)}
                                initial={{ opacity: 0, y: 12 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.45, delay: i * 0.06 }}
                                className="stat"
                            >
                                <div className="n">{s.n}</div>
                                <div className="l">{s.l}</div>
                            </motion.div>
                        ))}
                    </div>
                </div>
                <style>{`
                    @media (max-width: 760px) {
                        .stats-grid { grid-template-columns: 1fr 1fr !important; }
                    }
                `}</style>
            </section>

            {/* CTA BAND */}
            <section style={{ padding: "70px 0 90px" }}>
                <div className="wrap">
                    <div data-testid={TESTIDS.home.ctaBand} className="ctaband">
                        <h2
                            className="font-display"
                            style={{
                                color: "#fff",
                                fontSize: "clamp(1.6rem, 3vw, 2.4rem)",
                                marginBottom: 22,
                                fontWeight: 600,
                                letterSpacing: "-.02em",
                                maxWidth: "26ch",
                                margin: "0 auto 22px",
                            }}
                        >
                            Tell us your use case. We&apos;ll build the agent.
                        </h2>
                        <button
                            data-testid={TESTIDS.home.ctaBandBtn}
                            className="btn btn-light"
                            onClick={() => openModal({ source: "home-ctaband" })}
                        >
                            Book a Demo <ArrowRight size={14} />
                        </button>
                    </div>
                </div>
            </section>
        </>
    );
}
